'use client';

import { useTranslation } from 'react-i18next';

import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { knownMyUrls } from '@sola/my-sdk/constants.ts';
import { useAccountSettings } from '@sola/supporter-sdk/hooks/settings.ts';

import {
  AccountSettingsEmailPhone,
  AccountSettingsInformation,
  AccountSettingsMailingAddress,
  AccountSettingsProfile,
  AccountSettingsUsernamePassword,
  SettingDivider,
} from '../../molecules/account-settings-section/account-settings-section.tsx';
import { PrimerError } from '../../molecules/primer-error/primer-error.tsx';
import { AccountSettingsSkeleton } from './account-settings-skeleton.tsx';

export const AccountSettings = () => {
  const {
    supporter,
    supporterAddress,
    preferredUsername,
    contacts,
    showSkeleton,
    hasError,
  } = useAccountSettings();
  const { t } = useTranslation(['my-settings', 'my']);
  const hasSupporter = !!supporter;
  const hasAddress = !!supporterAddress;
  const nonPrimaryContacts = contacts?.filter((c) => !c.isPrimary) ?? [];

  return (
    <div className="max-w-md">
      {hasError && (
        <Alert
          variant="negative"
          title={t('my-settings:account.error.title')}
          message={t('my-settings:account.error.message')}
          action={
            <a href={t('my:ctas.contactSupport.url')}>
              {t('my:ctas.contactSupport.text')}
            </a>
          }
        />
      )}
      <PrimerError keysToAlert={['details', 'addressDetails']} />
      {!showSkeleton && hasSupporter && hasAddress && (
        <>
          <AccountSettingsInformation supporter={supporter} />
          <SettingDivider />
          <AccountSettingsProfile supporter={supporter} />
          {/* TODO: Should show an error message in the section if we cannot retrieve username */}
          {preferredUsername && (
            <>
              <SettingDivider />
              <AccountSettingsUsernamePassword username={preferredUsername} />
            </>
          )}
          <SettingDivider />
          <AccountSettingsMailingAddress addressDetails={supporterAddress} />
          <SettingDivider />
          <AccountSettingsEmailPhone supporter={supporter} />
          {nonPrimaryContacts.length > 0 && (
            <Alert
              variant="neutral"
              title={t('my-settings:account.additionalContacts', {
                count: nonPrimaryContacts.length,
              })}
              message={t('my-settings:account.additionalContactsText', {
                count: nonPrimaryContacts.length,
              })}
              action={
                <a href={knownMyUrls.settingsContact}>
                  {t('my-settings:account.contactLink')}
                </a>
              }
            />
          )}
        </>
      )}
      {showSkeleton && <AccountSettingsSkeleton />}
    </div>
  );
};
